import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ToolSubmenu } from '../components/ui/ToolSubmenu';

const COLORS = [
  { id: 'Yellow', value: '#eab308' },
  { id: 'White', value: '#fafafa' },
  { id: 'Red', value: '#ef4444' },
  { id: 'Blue', value: '#3b82f6' },
  { id: 'Green', value: '#22c55e' },
];

const FONT_MIN = 12;
const FONT_MAX = 72;
const DEFAULT_FONT_SIZE = 24;
const TEXT_FONT = '"Segoe UI", system-ui, sans-serif';

const ToolChip = ({ active, label, onClick, children, layout }) => (
  <button
    type="button"
    title={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    onPointerDown={(e) => e.stopPropagation()}
    className={`m-0 inline-flex h-7 items-center gap-1.5 rounded-sm border px-2 text-[10px] font-semibold uppercase tracking-wider transition-colors shrink-0 ${
      layout === 'column' ? 'w-full justify-start' : ''
    } ${
      active
        ? 'border-yellow-400 bg-yellow-500 text-black'
        : 'border-yellow-600/30 bg-zinc-950 text-yellow-400 hover:border-yellow-400 hover:bg-zinc-900'
    }`}
  >
    {children}
    <span className={layout === 'column' ? 'inline' : 'hidden sm:inline'}>{label}</span>
  </button>
);

const getCanvasPoint = (e, canvas) => {
  const rect = canvas.getBoundingClientRect();
  const w = rect.width || 1;
  const h = rect.height || 1;
  return [
    ((e.clientX - rect.left) / w) * canvas.width,
    ((e.clientY - rect.top) / h) * canvas.height,
  ];
};

const canvasToCss = (canvas, x, y) => {
  const rect = canvas.getBoundingClientRect();
  const w = canvas.width || 1;
  const h = canvas.height || 1;
  return {
    left: (x / w) * rect.width,
    top: (y / h) * rect.height,
  };
};

const clampFontSize = (value) =>
  Math.min(FONT_MAX, Math.max(FONT_MIN, Math.round(Number(value) || DEFAULT_FONT_SIZE)));

const fontFor = (size) => `${clampFontSize(size)}px ${TEXT_FONT}`;

const getTextBounds = (ctx, line) => {
  const size = clampFontSize(line.fontSize);
  ctx.font = fontFor(size);
  const width = Math.max(ctx.measureText(line.text || ' ').width, size * 0.5);
  const height = size * 1.25;
  return { x: line.x, y: line.y, w: width, h: height };
};

const hitTestText = (ctx, lines, x, y) => {
  for (let i = (lines || []).length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    if (!line || line.type !== 'text') continue;
    const box = getTextBounds(ctx, line);
    const pad = Math.max(6, (line.fontSize || DEFAULT_FONT_SIZE) * 0.2);
    if (x >= box.x - pad && x <= box.x + box.w + pad && y >= box.y - pad && y <= box.y + box.h + pad) {
      return i;
    }
  }
  return -1;
};

const drawTextLine = (ctx, line, selected) => {
  if (!line?.text) return;
  ctx.font = fontFor(line.fontSize);
  ctx.fillStyle = line.color || '#eab308';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(line.text, line.x, line.y);
  if (selected) {
    const box = getTextBounds(ctx, line);
    ctx.save();
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(box.x - 3, box.y - 2, box.w + 6, box.h + 4);
    ctx.restore();
  }
};

const Scribble = ({ isScribbleMode, selectedColor, onColorChange, layout = 'row', onClose }) => {
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const linesRef = useRef([]);
  const selectedIndexRef = useRef(-1);
  const dragRef = useRef(null);
  const draftRef = useRef(null);
  const [, setLinesTick] = useState(0);
  const [drawingMode, setDrawingMode] = useState('freehand');
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [, setSelectedIndexState] = useState(-1);
  const [draftText, setDraftTextState] = useState(null);
  const [hoverText, setHoverText] = useState(false);
  const isDrawing = useRef(false);
  const pointerId = useRef(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const lastX = useRef(0);
  const lastY = useRef(0);
  const [drawSlot, setDrawSlot] = useState(null);

  const setSelectedIndex = useCallback((index) => {
    selectedIndexRef.current = index;
    setSelectedIndexState(index);
  }, []);

  const setDraftText = useCallback((next) => {
    const value = typeof next === 'function' ? next(draftRef.current) : next;
    draftRef.current = value;
    setDraftTextState(value);
  }, []);

  useLayoutEffect(() => {
    if (!isScribbleMode) {
      setDrawSlot(null);
      return undefined;
    }
    const sync = () => setDrawSlot(document.getElementById('mdv-draw-submenu-slot'));
    sync();
    const id = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(id);
  }, [isScribbleMode, layout]);

  const syncLines = useCallback((next) => {
    linesRef.current = next;
    setLinesTick((t) => t + 1);
  }, []);

  const redrawLines = useCallback((lineHistory, selected = selectedIndexRef.current) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    (lineHistory || []).forEach((line, index) => {
      if (!line) return;
      if (line.type === 'text') {
        drawTextLine(ctx, line, index === selected);
        return;
      }
      ctx.strokeStyle = line.color || '#eab308';
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      if (line.type === 'freehand' && Array.isArray(line.points) && line.points.length) {
        line.points.forEach(([x, y], i) => {
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
      } else if (line.type === 'oval') {
        const { x, y, radiusX, radiusY } = line;
        ctx.ellipse(x, y, Math.max(0.5, radiusX), Math.max(0.5, radiusY), 0, 0, Math.PI * 2);
      } else if (line.type === 'rectangle') {
        const { x, y, width, height } = line;
        ctx.rect(x, y, width, height);
      } else {
        return;
      }
      ctx.stroke();
    });
  }, []);

  const patchLine = useCallback((index, patch) => {
    if (index < 0) return;
    const history = linesRef.current;
    const current = history[index];
    if (!current) return;
    const next = history.slice();
    next[index] = { ...current, ...patch };
    syncLines(next);
    redrawLines(next, index);
  }, [redrawLines, syncLines]);

  const commitDraftText = useCallback(() => {
    const draft = draftRef.current;
    if (!draft) return false;
    const value = String(draft.value || '').trim();
    setDraftText(null);
    if (!value) return false;
    const next = [
      ...linesRef.current,
      {
        type: 'text',
        color: selectedColor,
        x: draft.x,
        y: draft.y,
        text: value,
        fontSize: clampFontSize(draft.fontSize || fontSize),
      },
    ];
    syncLines(next);
    setSelectedIndex(next.length - 1);
    redrawLines(next, next.length - 1);
    return true;
  }, [fontSize, redrawLines, selectedColor, setDraftText, setSelectedIndex, syncLines]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
    setDraftText(null);
    setSelectedIndex(-1);
    syncLines([]);
  }, [setSelectedIndex, syncLines]);

  const undoLastLine = useCallback(() => {
    const next = linesRef.current.slice(0, -1);
    const selected = selectedIndexRef.current >= next.length ? -1 : selectedIndexRef.current;
    setSelectedIndex(selected);
    syncLines(next);
    redrawLines(next, selected);
  }, [redrawLines, setSelectedIndex, syncLines]);

  useEffect(() => {
    if (!isScribbleMode) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const fit = () => {
      const parent = canvas.parentElement;
      const w = parent?.clientWidth || window.innerWidth;
      const h = parent?.clientHeight || window.innerHeight;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        redrawLines(linesRef.current);
      }
    };

    fit();
    window.addEventListener('resize', fit);

    const preventScroll = (e) => e.preventDefault();
    canvas.addEventListener('touchstart', preventScroll, { passive: false });
    canvas.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      window.removeEventListener('resize', fit);
      canvas.removeEventListener('touchstart', preventScroll);
      canvas.removeEventListener('touchmove', preventScroll);
    };
  }, [isScribbleMode, redrawLines]);

  useEffect(() => {
    if (!isScribbleMode) {
      isDrawing.current = false;
      pointerId.current = null;
      dragRef.current = null;
      setDraftText(null);
      setSelectedIndex(-1);
    }
  }, [isScribbleMode, setSelectedIndex]);

  useEffect(() => {
    if (!draftText) return undefined;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [draftText]);

  const applyFontSize = (nextSize) => {
    const size = clampFontSize(nextSize);
    setFontSize(size);
    const index = selectedIndexRef.current;
    if (index >= 0 && linesRef.current[index]?.type === 'text') {
      patchLine(index, { fontSize: size });
    }
    setDraftText((draft) => (draft ? { ...draft, fontSize: size } : draft));
  };

  const applyColor = (color) => {
    onColorChange(color);
    const index = selectedIndexRef.current;
    if (index >= 0 && linesRef.current[index]?.type === 'text') {
      patchLine(index, { color });
    }
  };

  const chooseMode = (mode) => {
    commitDraftText();
    if (mode !== 'text') setSelectedIndex(-1);
    setDrawingMode(mode);
    redrawLines(linesRef.current, mode === 'text' ? selectedIndexRef.current : -1);
  };

  const startDrawing = (e) => {
    if (!isScribbleMode) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (pointerId.current != null && pointerId.current !== e.pointerId) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    e.preventDefault();
    e.stopPropagation();

    const [x, y] = getCanvasPoint(e, canvas);
    startX.current = x;
    startY.current = y;
    lastX.current = x;
    lastY.current = y;

    if (drawingMode === 'text') {
      commitDraftText();
      const ctx = canvas.getContext('2d');
      const hit = ctx ? hitTestText(ctx, linesRef.current, x, y) : -1;
      if (hit >= 0) {
        const line = linesRef.current[hit];
        setSelectedIndex(hit);
        setFontSize(clampFontSize(line.fontSize));
        dragRef.current = { index: hit, dx: x - line.x, dy: y - line.y };
        isDrawing.current = true;
        pointerId.current = e.pointerId;
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort on some browsers */
        }
        redrawLines(linesRef.current, hit);
        return;
      }
      setSelectedIndex(-1);
      redrawLines(linesRef.current, -1);
      setDraftText({ x, y, value: '', fontSize });
      return;
    }

    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort on some browsers */
    }

    pointerId.current = e.pointerId;
    isDrawing.current = true;

    if (drawingMode === 'freehand') {
      syncLines([
        ...linesRef.current,
        {
          type: 'freehand',
          color: selectedColor,
          points: [[x, y]],
        },
      ]);
    }
  };

  const draw = (e) => {
    if (!isScribbleMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const [currentX, currentY] = getCanvasPoint(e, canvas);

    if (drawingMode === 'text' && !isDrawing.current) {
      setHoverText(hitTestText(ctx, linesRef.current, currentX, currentY) >= 0);
      return;
    }

    if (!isDrawing.current) return;
    if (pointerId.current != null && e.pointerId !== pointerId.current) return;

    e.preventDefault();
    lastX.current = currentX;
    lastY.current = currentY;

    if (drawingMode === 'text' && dragRef.current) {
      const { index, dx, dy } = dragRef.current;
      patchLine(index, { x: currentX - dx, y: currentY - dy });
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    redrawLines(linesRef.current);

    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    if (drawingMode === 'freehand') {
      const history = linesRef.current;
      const last = history[history.length - 1];
      if (!last || last.type !== 'freehand' || !Array.isArray(last.points)) return;
      const next = history.slice();
      next[next.length - 1] = {
        ...last,
        points: [...last.points, [currentX, currentY]],
      };
      linesRef.current = next;
      setLinesTick((t) => t + 1);

      const prev = last.points[last.points.length - 1] || [currentX, currentY];
      ctx.beginPath();
      ctx.moveTo(prev[0], prev[1]);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();
    } else if (drawingMode === 'oval') {
      const radiusX = Math.abs(currentX - startX.current) / 2;
      const radiusY = Math.abs(currentY - startY.current) / 2;
      const centerX = (startX.current + currentX) / 2;
      const centerY = (startY.current + currentY) / 2;
      ctx.beginPath();
      ctx.ellipse(centerX, centerY, Math.max(0.5, radiusX), Math.max(0.5, radiusY), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (drawingMode === 'rectangle') {
      ctx.beginPath();
      ctx.rect(startX.current, startY.current, currentX - startX.current, currentY - startY.current);
      ctx.stroke();
    }
  };

  const stopDrawing = (e) => {
    if (!isScribbleMode || !isDrawing.current) return;
    if (e && pointerId.current != null && e.pointerId !== pointerId.current) return;

    const canvas = canvasRef.current;
    if (e) e.preventDefault();
    if (canvas && e?.pointerId != null) {
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    }

    isDrawing.current = false;
    pointerId.current = null;

    if (drawingMode === 'text') {
      dragRef.current = null;
      redrawLines(linesRef.current);
      return;
    }

    const [endX, endY] = canvas && e
      ? getCanvasPoint(e, canvas)
      : [lastX.current, lastY.current];

    if (drawingMode === 'oval') {
      const radiusX = Math.abs(endX - startX.current) / 2;
      const radiusY = Math.abs(endY - startY.current) / 2;
      syncLines([
        ...linesRef.current,
        {
          type: 'oval',
          color: selectedColor,
          x: (startX.current + endX) / 2,
          y: (startY.current + endY) / 2,
          radiusX,
          radiusY,
        },
      ]);
      redrawLines(linesRef.current);
    } else if (drawingMode === 'rectangle') {
      syncLines([
        ...linesRef.current,
        {
          type: 'rectangle',
          color: selectedColor,
          x: startX.current,
          y: startY.current,
          width: endX - startX.current,
          height: endY - startY.current,
        },
      ]);
      redrawLines(linesRef.current);
    } else {
      redrawLines(linesRef.current);
    }
  };

  const editSelectedText = (e) => {
    if (drawingMode !== 'text') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const [x, y] = getCanvasPoint(e, canvas);
    const hit = hitTestText(ctx, linesRef.current, x, y);
    if (hit < 0) return;
    e.preventDefault();
    e.stopPropagation();
    const line = linesRef.current[hit];
    const next = linesRef.current.filter((_, i) => i !== hit);
    syncLines(next);
    setSelectedIndex(-1);
    setFontSize(clampFontSize(line.fontSize));
    setDraftText({
      x: line.x,
      y: line.y,
      value: line.text || '',
      fontSize: clampFontSize(line.fontSize),
    });
    redrawLines(next, -1);
  };

  if (!isScribbleMode) return null;

  const iconProps = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    'aria-hidden': true,
  };

  const draftCss = draftText && canvasRef.current
    ? canvasToCss(canvasRef.current, draftText.x, draftText.y)
    : null;
  const draftPx = clampFontSize(draftText?.fontSize || fontSize);

  const toolbar = (
    <div onPointerDown={(e) => e.stopPropagation()}>
      <ToolSubmenu layout={layout} label="Draw" onClose={onClose}>
        <div
          className={`flex items-center gap-1 shrink-0 ${layout === 'column' ? 'flex-wrap w-full' : 'mr-1'}`}
        >
          {COLORS.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-label={c.id}
              title={c.id}
              onClick={(e) => {
                e.stopPropagation();
                applyColor(c.value);
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="m-0 rounded-full border-2 p-0"
              style={{
                width: 18,
                height: 18,
                backgroundColor: c.value,
                borderColor: selectedColor === c.value ? '#fde047' : '#3f3f46',
                boxShadow: selectedColor === c.value ? '0 0 0 1px #eab308' : 'none',
              }}
            />
          ))}
        </div>

        <ToolChip layout={layout} label="Undo" onClick={undoLastLine}>
          <svg {...iconProps}>
            <path d="M9 14L4 9l5-5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 9h10a5 5 0 010 10h-2" strokeLinecap="round" />
          </svg>
        </ToolChip>

        <ToolChip layout={layout} label="Clear" onClick={clearCanvas}>
          <svg {...iconProps}>
            <path d="M5 7h14M9 7V5h6v2M8 7l1 12h6l1-12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ToolChip>

        {layout === 'row' && <div className="w-px h-4 bg-yellow-600/25 mx-0.5 shrink-0" />}

        <ToolChip
          layout={layout}
          label="Freehand"
          active={drawingMode === 'freehand'}
          onClick={() => chooseMode('freehand')}
        >
          <svg {...iconProps}>
            <path d="M4 20l4.5-1.2L19 8.3a2.1 2.1 0 10-3-3L5.5 15.8 4 20z" strokeLinejoin="round" />
          </svg>
        </ToolChip>

        <ToolChip
          layout={layout}
          label="Oval"
          active={drawingMode === 'oval'}
          onClick={() => chooseMode('oval')}
        >
          <svg {...iconProps}>
            <ellipse cx="12" cy="12" rx="8" ry="5.5" />
          </svg>
        </ToolChip>

        <ToolChip
          layout={layout}
          label="Rectangle"
          active={drawingMode === 'rectangle'}
          onClick={() => chooseMode('rectangle')}
        >
          <svg {...iconProps}>
            <rect x="5" y="7" width="14" height="10" rx="1" />
          </svg>
        </ToolChip>

        <ToolChip
          layout={layout}
          label="Text"
          active={drawingMode === 'text'}
          onClick={() => chooseMode('text')}
        >
          <svg {...iconProps}>
            <path d="M5 6h14M12 6v12M8 18h8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ToolChip>

        {drawingMode === 'text' && (
          <label
            className={`flex items-center gap-1.5 shrink-0 ${
              layout === 'column' ? 'w-full px-0.5' : 'ml-1'
            }`}
          >
            <span className="text-[9px] uppercase tracking-wider text-zinc-500 shrink-0">Size</span>
            <input
              type="range"
              min={FONT_MIN}
              max={FONT_MAX}
              step={2}
              value={fontSize}
              onChange={(e) => applyFontSize(e.target.value)}
              onPointerDown={(e) => e.stopPropagation()}
              className="m-0 accent-yellow-500 min-w-0 flex-1"
              aria-label="Text size"
            />
            <span className="text-[10px] tabular-nums text-yellow-400 w-6 text-right shrink-0">{fontSize}</span>
          </label>
        )}
      </ToolSubmenu>
    </div>
  );

  const toolbarMounted = drawSlot ? createPortal(toolbar, drawSlot) : toolbar;

  return (
    <>
      {toolbarMounted}
      <div className="absolute inset-0 z-[50] h-full w-full">
        <canvas
          id="mdv-draw-canvas"
          ref={canvasRef}
          className="absolute inset-0 h-full w-full touch-none"
          style={{
            pointerEvents: 'auto',
            touchAction: 'none',
            cursor: drawingMode === 'text' ? (hoverText ? 'move' : 'text') : 'crosshair',
          }}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onDoubleClick={editSelectedText}
        />
        {draftText && draftCss && (
          <input
            ref={inputRef}
            value={draftText.value}
            onChange={(e) => setDraftText((d) => ({ ...d, value: e.target.value }))}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitDraftText();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setDraftText(null);
              }
            }}
            placeholder="Type…"
            className="absolute m-0 bg-black/40 outline-none border border-dashed border-yellow-400/80 rounded-sm px-1 py-0 text-left"
            style={{
              left: draftCss.left,
              top: draftCss.top,
              fontSize: `${draftPx}px`,
              lineHeight: 1.25,
              color: selectedColor,
              fontFamily: 'Segoe UI, system-ui, sans-serif',
              minWidth: `${Math.max(8, draftText.value.length + 2)}ch`,
              caretColor: selectedColor,
            }}
          />
        )}
      </div>
    </>
  );
};

export default Scribble;
